/**
 * Event-image SAS provider.
 *
 * The storage account that hosts event images does not allow anonymous public
 * access. To render images in <img> tags (which can't carry an Authorization
 * header), the SPA fetches a short-lived read-only container SAS from the API
 * and appends it as a query string to every blob URL.
 *
 * - Cached in module scope, refreshed ~5 minutes before expiry.
 * - Only fetched once per session for authenticated users; anonymous callers
 *   never trigger a fetch.
 */
import { useEffect, useState } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { api, isAuthPending } from './api';

interface SasState {
  sas: string;
  expiresAt: number;
}

let cached: SasState | null = null;
let inflight: Promise<SasState | null> | null = null;
const subscribers = new Set<() => void>();

const REFRESH_BUFFER_MS = 5 * 60 * 1000;

async function fetchSas(): Promise<SasState | null> {
  try {
    const res = await api.get<{ sas: string; expiresAt: string }>('/api/mgmt/images/event-sas');
    return { sas: res.sas, expiresAt: new Date(res.expiresAt).getTime() };
  } catch (e) {
    if (isAuthPending(e)) return null;
    // Don't surface — image rendering is best-effort.
    return null;
  }
}

async function ensureSas(): Promise<SasState | null> {
  if (cached && cached.expiresAt - Date.now() > REFRESH_BUFFER_MS) return cached;
  if (inflight) return inflight;
  inflight = fetchSas().then((s) => {
    cached = s;
    inflight = null;
    subscribers.forEach((cb) => cb());
    return s;
  });
  return inflight;
}

function appendSas(url: string, sas: string): string {
  if (url.includes('sig=')) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}${sas}`;
}

/**
 * Hook: returns a function that resolves a raw blob URL to a SAS-signed URL.
 * For anonymous users (or while the SAS is still loading), the resolver
 * returns `null` so callers can omit the image entirely — the storage account
 * 409s on anonymous reads.
 */
export function useEventImageUrl(): (raw: string | undefined) => string | null {
  const isAuthed = useIsAuthenticated();
  const [, force] = useState(0);

  useEffect(() => {
    if (!isAuthed) return;
    const cb = () => force((n) => n + 1);
    subscribers.add(cb);
    void ensureSas();
    return () => {
      subscribers.delete(cb);
    };
  }, [isAuthed]);

  return (raw) => {
    if (!raw) return null;
    if (!isAuthed) return null;
    if (!cached || cached.expiresAt - Date.now() <= REFRESH_BUFFER_MS) {
      void ensureSas();
      return null;
    }
    return appendSas(raw, cached.sas);
  };
}

/** Drop the cached SAS (e.g. on sign-out). */
export function clearImageSas(): void {
  cached = null;
  inflight = null;
  subscribers.forEach((cb) => cb());
}
